import { useState, useRef, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { autoCreateLead } from '../lib/crm';
import { MarkdownRenderer } from '../components/ai/MarkdownRenderer';

const TM = () => <sup className="text-[0.6em] font-normal align-super">™</sup>;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface CoachLeadPage {
  id: string;
  coach_id: string;
  headline: string;
  description: string;
  cta_text: string;
  image_url: string | null;
  booking_url: string | null;
  assessment_code: string | null;
  conversation_headline: string | null;
  conversation_active: boolean;
}

const steps = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    text: 'Tell it what\'s costing you sleep right now.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    text: 'Get a real coaching response — in your own words.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    ),
    text: 'Choose to go deeper with the free profile or a 30-minute call.',
  },
];

const CONVERSATION_TURN_THRESHOLD = 6;

export function CoachConversationPage() {
  const { slug } = useParams<{ slug: string }>();
  const [showModal, setShowModal] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [showConversion, setShowConversion] = useState(false);
  const [coachPage, setCoachPage] = useState<CoachLeadPage | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) { setPageLoading(false); return; }

    const loadCoachPage = async () => {
      const { data } = await supabase
        .from('coach_lead_pages')
        .select('id, coach_id, headline, description, cta_text, image_url, booking_url, assessment_code, conversation_headline, conversation_active')
        .eq('slug', slug)
        .maybeSingle();

      if (data) setCoachPage(data as CoachLeadPage);
      setPageLoading(false);
    };

    loadCoachPage();
  }, [slug]);

  useEffect(() => {
    if (chatStarted) {
      setTimeout(() => {
        chatRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        inputRef.current?.focus();
      }, 300);
    }
  }, [chatStarted]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStartConversation = () => {
    setShowModal(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) { setFormError('Please enter your first name.'); return; }
    if (!formEmail.trim() || !formEmail.includes('@')) { setFormError('Please enter a valid email address.'); return; }
    setFormError('');
    setUserName(formName.trim());
    setUserEmail(formEmail.trim());
    setShowModal(false);
    setChatStarted(true);

    if (coachPage?.coach_id) {
      autoCreateLead(coachPage.coach_id, {
        full_name: formName.trim(),
        email: formEmail.trim(),
        source: 'ai_coach_trial',
      }).catch(() => {});
    }
  };

  const sendMessage = async (messageText?: string) => {
    const text = (messageText ?? input).trim();
    if (!text || isLoading) return;

    const isFirst = messages.length === 0;
    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setChatError(null);

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));

      const { data, error } = await supabase.functions.invoke('lead-magnet-chat', {
        body: {
          message: text,
          name: userName,
          email: userEmail,
          conversation_history: history,
          is_first_message: isFirst,
          coach_id: coachPage?.coach_id || undefined,
        },
      });

      if (error) throw new Error(error.message || 'Failed to get response');
      if (!data?.response) throw new Error('No response received');

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.response,
      };
      setMessages(prev => [...prev, assistantMsg]);

      const totalTurns = messages.length + 2;
      if (totalTurns >= CONVERSATION_TURN_THRESHOLD && !showConversion) {
        setTimeout(() => setShowConversion(true), 1500);
      }
    } catch (err: any) {
      setChatError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const bookingUrl = coachPage?.booking_url || undefined;
  const profileUrl = coachPage?.assessment_code
    ? `/register?code=${encodeURIComponent(coachPage.assessment_code)}`
    : '/register';
  const heroHeadline = coachPage?.conversation_headline || coachPage?.headline || undefined;

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-sage-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f4] font-urbanist text-chocolate-800">

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#f3efe6]/95 backdrop-blur border-b border-chocolate-200/30">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center">
          <Link to="/">
            <img src="/nlp_os_logo_off_white_creme_bg_.png" alt="NLP OS" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      {/* SECTION 1 — HERO */}
      <section className="max-w-5xl mx-auto px-5 pt-16 pb-12 lg:pt-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <motion.div
            className="space-y-6 text-center lg:text-left"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <p className="text-sage-600 font-semibold text-sm tracking-widest uppercase">
              Free AI Coaching Session
            </p>
            <h1 className="text-4xl lg:text-5xl font-bold text-chocolate-900 leading-[1.15]">
              {heroHeadline ? (
                heroHeadline
              ) : (
                <>
                  You're stuck on something.<br className="hidden lg:block" /> You can feel it.<br />
                  Spend three minutes talking it through.
                </>
              )}
            </h1>
            <p className="text-chocolate-600 text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Tell our AI Coach what's costing you sleep right now. Get a real coaching response — not a chatbot.
              Then choose to go deeper with a free NLP OS<TM /> Profile or a call with the human coach.
            </p>
            <div>
              <button
                onClick={handleStartConversation}
                className="inline-flex items-center gap-2 bg-sage-600 hover:bg-sage-700 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-base tracking-wide"
              >
                Start the Conversation
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </motion.div>

          <motion.div
            className="rounded-2xl overflow-hidden shadow-xl"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut', delay: 0.15 }}
          >
            <img
              src="/images/lead-magnet/hpos_ai_coach_hero_visual.png"
              alt="AI coaching conversation"
              className="w-full h-auto object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — WHAT HAPPENS */}
      <section className="bg-white/70 py-14">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="rounded-2xl overflow-hidden shadow-lg"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <img
                src="/images/lead-magnet/hpos_ai_coach_three_steps_visual.png"
                alt="Three steps of the AI coaching session"
                className="w-full h-auto object-cover"
              />
            </motion.div>
            <div className="space-y-5">
              {steps.map((step, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-4"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i * 0.5}
                  variants={fadeUp}
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-sage-100 text-sage-600 flex items-center justify-center">
                    {step.icon}
                  </div>
                  <p className="text-chocolate-700 text-lg leading-snug pt-1.5">{step.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHY THIS IS DIFFERENT */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="space-y-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              {[
                'Most AI tools give you generic advice. This one is trained on a real coach\'s voice, frameworks, and decades of experience.',
                'You\'ll feel the difference in the first message. Not a chatbot. Not a script. A coaching conversation.',
                'And at the end, you\'ll have a clear next step that fits where you actually are.',
              ].map((para, i) => (
                <p
                  key={i}
                  className={`text-chocolate-700 text-lg leading-relaxed ${i === 1 ? 'font-semibold text-chocolate-900' : ''}`}
                >
                  {para}
                </p>
              ))}
            </motion.div>
            <motion.div
              className="rounded-2xl overflow-hidden shadow-lg"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              variants={fadeUp}
            >
              <img
                src="/images/lead-magnet/hpos_ai_coach_not_a_chatbot_visual.png"
                alt="Not a chatbot — a real coaching conversation"
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — TRUST NOTE + CHAT ENTRY */}
      <section className="bg-white/70 py-14">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="space-y-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="bg-cream-50 border border-cream-200 rounded-xl px-6 py-5 space-y-2 text-sm text-chocolate-600 leading-relaxed">
                <p className="font-semibold text-chocolate-800">A note before you start.</p>
                <p>
                  The AI Coach is a coaching companion trained on real coaching frameworks. It's designed to help you think clearly
                  and move forward — not to diagnose, treat, or replace professional medical, psychological, legal, or financial advice.
                </p>
                <p>
                  If you're in crisis or need clinical support, please contact a qualified professional.
                </p>
                <p>
                  Everything you share here is private to you and the coach who runs this session.
                </p>
              </div>

              <div>
                <button
                  onClick={handleStartConversation}
                  className="inline-flex items-center gap-2 bg-sage-600 hover:bg-sage-700 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-base tracking-wide"
                >
                  Start the Conversation
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </motion.div>

            <motion.div
              className="rounded-2xl overflow-hidden shadow-lg"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              variants={fadeUp}
            >
              <img
                src="/images/lead-magnet/hpos_ai_coach_two_pathways_visual.png"
                alt="Two pathways — take the free profile or book a call"
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CHAT — appears after opt-in */}
      <AnimatePresence>
        {chatStarted && (
          <motion.section
            ref={chatRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="py-12 bg-[#faf8f4]"
          >
            <div className="max-w-2xl mx-auto px-5 space-y-6">
              <div className="rounded-2xl overflow-hidden shadow-md">
                <img
                  src="/images/lead-magnet/hpos_ai_coach_conversation_preview_visual.png"
                  alt="What a coaching conversation looks like"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="bg-white rounded-2xl border border-cream-200 shadow-lg overflow-hidden">
                {/* Chat header */}
                <div className="px-6 py-4 bg-gradient-to-r from-sage-600 to-sage-700 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5 text-white">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm">AI Coach</h3>
                      <p className="text-xs text-sage-100">NLP OS<TM /></p>
                    </div>
                    <div className="ml-auto flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-green-400 rounded-full" />
                      <span className="text-xs text-sage-100">Active</span>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="px-5 py-5 space-y-4 min-h-[320px] max-h-[520px] overflow-y-auto">
                  {messages.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-start"
                    >
                      <div className="max-w-[85%] bg-cream-100 rounded-2xl rounded-bl-md px-4 py-3">
                        <p className="text-sm text-chocolate-800 leading-relaxed">
                          Hi {userName} — good to meet you. I'm here to think things through with you, not give you a list of tips.
                        </p>
                        <p className="text-sm text-chocolate-800 leading-relaxed mt-2">
                          What's costing you sleep right now?
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-sage-600 text-white rounded-br-md'
                          : 'bg-cream-100 text-chocolate-800 rounded-bl-md'
                      }`}>
                        {msg.role === 'assistant' ? (
                          <div className="text-sm leading-relaxed prose prose-sm max-w-none">
                            <MarkdownRenderer content={msg.content} />
                          </div>
                        ) : (
                          <p className="text-sm leading-relaxed">{msg.content}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}

                  {isLoading && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                      <div className="bg-cream-100 rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Conversion CTA */}
                  {showConversion && !isLoading && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="pt-2"
                    >
                      <div className="bg-gradient-to-br from-sage-50 to-cream-50 border border-sage-200 rounded-2xl p-5 space-y-4">
                        <p className="text-sm font-semibold text-chocolate-900 text-center">
                          Ready for your next step?
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {bookingUrl && (
                            <a
                              href={bookingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 bg-sage-600 hover:bg-sage-700 text-white font-semibold px-5 py-3 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md text-sm"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Book a Free Call
                            </a>
                          )}
                          <Link
                            to={profileUrl}
                            className="inline-flex items-center justify-center gap-2 bg-white border border-sage-300 hover:border-sage-500 text-sage-700 font-semibold px-5 py-3 rounded-xl transition-all duration-200 text-sm"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            Take the Free Profile
                          </Link>
                        </div>
                        {!bookingUrl && (
                          <p className="text-xs text-chocolate-500 text-center">
                            Want to talk it through? Take the free profile and you'll get the option to speak with a coach.
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {chatError && (
                    <div className="text-center">
                      <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg inline-block">{chatError}</p>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="px-4 py-3 border-t border-cream-200 bg-cream-50">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Share what's on your mind..."
                      rows={2}
                      className="flex-1 resize-none border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400 bg-white"
                      style={{ maxHeight: '120px' }}
                    />
                    <button
                      onClick={() => sendMessage()}
                      disabled={!input.trim() || isLoading}
                      className="p-2.5 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-colors disabled:opacity-40 flex-shrink-0"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-[10px] text-chocolate-400 mt-2 text-center">
                    Press Enter to send · Shift+Enter for new line
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Cross-link to /discover */}
      <section className="border-t border-chocolate-200/40 bg-[#f5f2ec] py-10">
        <div className="max-w-2xl mx-auto px-5 text-center space-y-3">
          <p className="text-sm text-chocolate-500 leading-relaxed">
            Prefer to start with a structured self-assessment?{' '}
            <Link to={slug ? `/discover/${slug}` : '/discover'} className="text-sage-600 hover:text-sage-700 underline underline-offset-2 font-medium transition-colors">
              Take the free NLP OS<TM /> Profile instead
            </Link>
            {' '}— it takes about 15 minutes and gives you a personalised visual snapshot.
          </p>
          <p className="text-xs text-chocolate-400 leading-relaxed max-w-xl mx-auto">
            The AI Coach is a coaching companion designed for reflection and personal development — not a substitute for professional medical,
            psychological, legal, or financial advice. Everything shared here is private.
          </p>
        </div>
      </section>

      {/* SECTION 5 — OPT-IN MODAL */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-chocolate-900/50 backdrop-blur-sm z-40"
              onClick={() => setShowModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4"
            >
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 space-y-6 relative">
                <div className="space-y-1">
                  <h2 className="text-2xl font-bold text-chocolate-900">Let's begin.</h2>
                  <p className="text-sm text-chocolate-500">No account needed. Just a name and email so we can follow up if you'd like to go further.</p>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-chocolate-600 uppercase tracking-wide">First name</label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="e.g. Alex"
                      autoFocus
                      className="w-full border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-chocolate-600 uppercase tracking-wide">Email</label>
                    <input
                      type="email"
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400"
                    />
                  </div>

                  {formError && (
                    <p className="text-xs text-red-600">{formError}</p>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-sage-600 hover:bg-sage-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm tracking-wide flex items-center justify-center gap-2"
                  >
                    Start the Conversation
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <p className="text-xs text-chocolate-400 text-center leading-relaxed">
                    No spam. No sales calls. Your information stays private.
                  </p>
                </form>

                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-4 right-4 p-1.5 text-chocolate-400 hover:text-chocolate-600 transition-colors"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}
